import Tesseract from 'tesseract.js';
import type { IndexedCertificate, OCRProgressEvent } from '../types';

const supportedMimeTypes = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
]);

const certificateCatalog: IndexedCertificate[] = [];

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unsupported file encoding'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const runImageOcr = async (file: File, onProgress?: (stage: OCRProgressEvent['stage']) => void) => {
  onProgress?.('reading');
  const dataUrl = await readFileAsDataUrl(file);
  onProgress?.('ocr');

  const result = await Tesseract.recognize(dataUrl, 'eng', {
    logger: () => {
      /* suppress noisy console logging */
    },
  });

  const { text, confidence } = result.data;

  return {
    text,
    confidence: typeof confidence === 'number' ? confidence : 0,
  };
};

export const indexCertificates = async (
  files: FileList | File[],
  onProgress?: (event: OCRProgressEvent) => void,
): Promise<IndexedCertificate[]> => {
  const queue = Array.from(files).filter((file) => supportedMimeTypes.has(file.type));
  const total = queue.length;

  if (!total) {
    return [];
  }

  const newlyIndexed: IndexedCertificate[] = [];
  let processed = 0;

  for (const file of queue) {
    onProgress?.({ fileName: file.name, current: processed, total, stage: 'queued' });
    const { text, confidence } = await runImageOcr(file, (stage) => {
      onProgress?.({ fileName: file.name, current: processed, total, stage });
    });

    const certificate: IndexedCertificate = {
      id: crypto.randomUUID(),
      file,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      uploadedAt: new Date().toISOString(),
      previewUrl: URL.createObjectURL(file),
      pages: [
        {
          pageNumber: 1,
          text,
          confidence,
        },
      ],
      fullText: text,
    };

    certificateCatalog.push(certificate);
    newlyIndexed.push(certificate);
    processed += 1;
    onProgress?.({ fileName: file.name, current: processed, total, stage: 'queued' });
  }

  return newlyIndexed;
};

export const searchCertificates = (query: string): IndexedCertificate[] => {
  const trimmed = query.trim().toLowerCase();

  if (!trimmed) {
    return [...certificateCatalog];
  }

  return certificateCatalog.filter((entry) => {
    const haystack = `${entry.fileName} ${entry.fullText}`.toLowerCase();
    return haystack.includes(trimmed);
  });
};

export const getCertificateById = (id: string) =>
  certificateCatalog.find((entry) => entry.id === id);

export const downloadCertificate = async (id: string) => {
  const record = getCertificateById(id);
  if (!record) {
    throw new Error('Certificate not found');
  }

  const url = URL.createObjectURL(record.file);
  const link = document.createElement('a');
  link.href = url;
  link.download = record.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const clearCatalog = () => {
  certificateCatalog.forEach((record) => URL.revokeObjectURL(record.previewUrl));
  certificateCatalog.splice(0, certificateCatalog.length);
};
